import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, ValidationErrors } from '@angular/forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { ODataModelChangeEvent } from '../../interfaces/odata-model.interface';
import { NumberModel } from '../../models/primitive/number.model';

@Component({
    selector: 'pace-core-number-stepper',
    templateUrl: './number-stepper.component.html',
    styleUrls: ['./number-stepper.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false,
})
export class NumberStepperComponent implements OnInit, OnChanges, OnDestroy {
    private previousValue: number | undefined;

    @Input()
    public model: NumberModel;

    @Output()
    public modelChange = new EventEmitter<NumberModel>();

    @Input()
    public parentForm: FormArray;

    @Output()
    public valueChange = new EventEmitter<ODataModelChangeEvent<NumberModel>>();

    @Input()
    public ariaLabel = 'Number Stepper Input';

    public numberStepperControl: FormControl;

    private destroySubject = new Subject<void>();
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly formBuilder: FormBuilder,
    ) {}

    public ngOnInit(): void {
        this.previousValue = this.model.value;
        const numberStepperValidator = (control: AbstractControl): ValidationErrors | null =>
            this.checkValidity(control);
        this.numberStepperControl = this.formBuilder.control(this.model.value, numberStepperValidator);

        this.numberStepperControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                tap((newVal: number) => {
                    this.model.value = newVal;
                    this.modelChange.emit(this.model);
                    this.valueChange.emit({
                        oldValue: this.previousValue,
                        newValue: this.model.value,
                    });
                }),
                takeUntil(this.destroySubject),
            )
            .subscribe();

        // The actual control needs to be added asynchronously to avoid an ExpressionChangedAfterItHasBeenCheckedError
        // See also https://stackoverflow.com/questions/45661010/dynamic-nested-reactive-form-expressionchangedafterithasbeencheckederror
        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.numberStepperControl);
                })
                .catch((error: Error) =>
                    this.log.error(
                        'Error in NumberStepperComponent -> ngOnInit -> parentForm.push: ',
                        JSON.stringify(error),
                    ),
                );
        }
    }

    public ngOnChanges(changesObj: SimpleChanges): void {
        if (changesObj && changesObj.model && this.numberStepperControl) {
            if (this.model.options.readonly && this.numberStepperControl.enabled) {
                this.numberStepperControl.disable({ onlySelf: true, emitEvent: false });
            }
            if (!this.model.options.readonly && this.numberStepperControl.disabled) {
                this.numberStepperControl.enable({ onlySelf: true, emitEvent: false });
            }

            this.model = <NumberModel>changesObj.model.currentValue;
            this.numberStepperControl.setValue(this.model.value);
        }
    }

    public ngOnDestroy(): void {
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.numberStepperControl);
            this.parentForm.removeAt(index);
        }
        this.destroySubject.next();
        this.destroySubject.complete();
    }

    public isIncDisabled(): boolean {
        if (this.model.options.readonly || typeof this.model.value === 'undefined') {
            return true;
        }

        return this.model.value - this.model.options.step < this.model.options.minValue;
    }

    public inc(): void {
        // Even though the linter says otherwise, 'potentialOldValue' is NOT redundant - this.model.inc() changes
        // this.model.value and we preserve the value *before* the change.
        if (this.model.inc(this.model.options.step)) {
            this.previousValue = this.model.value;
            this.numberStepperControl.setValue(this.model.value);
        }
    }

    public isDecDisabled(): boolean {
        if (this.model.options.readonly || typeof this.model.value === 'undefined') {
            return true;
        }

        return this.model.value + this.model.options.step > this.model.options.maxValue;
    }

    public dec(): void {
        // Even though the linter says otherwise, 'potentialOldValue' is NOT redundant - this.model.dec() changes
        // this.model.value and we preserve the value *before* the change.
        if (this.model.dec(this.model.options.step)) {
            this.previousValue = this.model.value;
            this.numberStepperControl.setValue(this.model.value);
        }
    }

    public checkValidity(control: AbstractControl): ValidationErrors | null {
        const errors: ValidationErrors = {};

        const controlValue = <number>control.value;

        if (this.model.options.required && !controlValue && controlValue !== 0) {
            errors.required = { required: 'this input is mandatory' };
        }
        if (controlValue > this.model.options.maxValue) {
            errors.max = { max: 'max value exceeded' };
        }
        if (controlValue < this.model.options.minValue) {
            errors.min = { max: 'min value exceeded' };
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }
}
